import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text, TextArea, TextField } from "@radix-ui/themes"
import { LoaderCircle } from "lucide-react"
import type { FormEventHandler } from "react"

import InputError from "@/components/input-error"
import LinkButton from "@/components/link-button"
import CenteredLayout from "@/layouts/centered-layout"
import { projectsPath } from "@/routes"

interface ProjectForm {
  name: string
  description: string
  sender_email: string
}

export default function NewProject() {
  const { data, setData, post, processing, errors } = useForm<Required<ProjectForm>>({
    name: "",
    description: "",
    sender_email: "",
  })

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(projectsPath())
  }

  return (
    <CenteredLayout>
      <Head title="New Project" />
      <Flex direction="column" gap="6" p="6">
        <Heading size="6" weight="medium">
          Create Project
        </Heading>

        <form className="flex flex-col gap-6" onSubmit={submit}>
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Flex justify="between">
                <Text as="label" htmlFor="name" size="2" weight="medium">
                  Project Name
                </Text>

                <InputError message={errors.name} />
              </Flex>

              <TextField.Root
                id="name"
                type="text"
                autoFocus
                tabIndex={1}
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="My awesome project"
              />
            </div>

            <div className="grid gap-2">
              <Text as="label" htmlFor="description" size="2" weight="medium">
                Description
              </Text>
              <TextArea
                id="description"
                tabIndex={2}
                value={data.description}
                onChange={(e) => setData("description", e.target.value)}
                placeholder="Describe your project"
              />
              <InputError message={errors.description} />
            </div>

            <div className="grid gap-2">
              <Flex justify="between">
                <Text as="label" htmlFor="sender_email" size="2" weight="medium">
                  Sender Email
                </Text>

                <InputError message={errors.sender_email} />
              </Flex>

              <TextField.Root
                id="sender_email"
                type="email"
                tabIndex={3}
                value={data.sender_email}
                onChange={(e) => setData("sender_email", e.target.value)}
                placeholder="noreply@yourproject.com"
              />
            </div>

            <Flex gap="3" mt="4">
              <Button type="submit" className="w-full" tabIndex={4} disabled={processing}>
                {processing && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Create project
              </Button>

              <LinkButton href={projectsPath()} variant="soft" className="w-full" tabIndex={5}>
                Cancel
              </LinkButton>
            </Flex>
          </div>
        </form>
      </Flex>
    </CenteredLayout>
  )
}
